import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const NOMES_MESES = [
    "JANEIRO", "FEVEREIRO", "MARCO", "ABRIL", "MAIO", "JUNHO",
    "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
];

const COLS_FIXAS = ["Conta", "Centro de Custo", "Grupo", "Tipo", "Unidade", "Responsável"];
const LARGURAS_FIXAS = [37, 20, 15, 17, 10, 18];

const FORMATO_MOEDA = '#,##0.00;[Red]-#,##0.00';

// Paleta espelhando o excel de referência: cada seção tem uma cor "forte" (linha de grupo)
// e uma cor "clara" (linha de subcabeçalho Orçado/Realizado/Saldo).
const CORES = {
    fixo: { forte: "FF2C3E50" },
    mes: { forte: "FF1F4E79", clara: "FF2E75B6" },
    acumulado: { forte: "FF7B68EE", clara: "FF9370DB", linha: "FFE6E6FA" },
    total: { forte: "FFC45911", clara: "FFED7D31", linha: "FFFFF2CC" },
    linhaFixa: "FFE8F4FD",
    totalGeral: "FFD9E2F3",
    bordaClara: "FFB7C6D9",
};

function estiloCabecalho(cor, tamanho = 12) {
    return {
        font: { name: "Calibri", size: tamanho, bold: true, color: { argb: "FFFFFFFF" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: cor } },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
    };
}

function bordaFina(cor = CORES.bordaClara) {
    return { style: "thin", color: { argb: cor } };
}

export async function exportarPrevistoRealizadoExcel(lista = [], mesesSelecionados = [], ano) {
    const mesesFiltrados = NOMES_MESES
        .map((nome, idx) => ({ nome, idx }))
        .filter(({ idx }) => mesesSelecionados.length === 0 || mesesSelecionados.includes(idx + 1));

    const totalColunas = COLS_FIXAS.length + mesesFiltrados.length * 3 + 6; // + Acumulado + Total

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Detalhamento BP", {
        views: [{ state: "frozen", xSplit: 3, ySplit: 2 }], // congela colunas A-C e as 2 linhas de cabeçalho
    });

    // ---------- Larguras de coluna ----------
    worksheet.columns = Array.from({ length: totalColunas }, (_, i) => ({
        width: i < COLS_FIXAS.length ? LARGURAS_FIXAS[i] : 13,
    }));

    // ---------- Linha 1 e 2 (cabeçalhos) ----------
    const linha1 = worksheet.getRow(1);
    const linha2 = worksheet.getRow(2);
    linha1.height = 28;
    linha2.height = 22;

    COLS_FIXAS.forEach((nome, i) => {
        const col = i + 1;
        worksheet.mergeCells(1, col, 2, col);
        const cell = worksheet.getCell(1, col);
        cell.value = nome;
        cell.style = estiloCabecalho(CORES.fixo.forte, 12);
        cell.border = { top: bordaFina(), left: bordaFina(), right: bordaFina(), bottom: { style: "medium", color: { argb: "FFFFFFFF" } } };
    });

    let col = COLS_FIXAS.length + 1;

    const aplicarGrupo = (titulo, cores, largura = 3) => {
        worksheet.mergeCells(1, col, 1, col + largura - 1);
        const cabecGrupo = worksheet.getCell(1, col);
        cabecGrupo.value = titulo;
        cabecGrupo.style = estiloCabecalho(cores.forte, 12);

        ["Orçado", "Realizado", "Saldo"].forEach((sub, i) => {
            const c = worksheet.getCell(2, col + i);
            c.value = sub;
            c.style = estiloCabecalho(cores.clara, 10);
            c.border = {
                bottom: { style: "medium", color: { argb: "FFFFFFFF" } },
                right: i === 2 ? { style: "medium", color: { argb: "FFFFFFFF" } } : bordaFina("FF5B84A8"),
            };
        });

        const inicio = col;
        col += largura;
        return inicio;
    };

    const inicioMeses = mesesFiltrados.map(({ nome }) => aplicarGrupo(nome, CORES.mes));
    const inicioAcumulado = aplicarGrupo("Acumulado", CORES.acumulado);
    const inicioTotal = aplicarGrupo("Total", CORES.total);

    // ---------- Linhas de dados ----------
    const totais = {
        acumuladoOrcado: 0, acumuladoRealizado: 0, acumuladoSaldo: 0,
        totalOrcado: 0, totalRealizado: 0, totalSaldo: 0,
        meses: mesesFiltrados.map(() => ({ valorOrcado: 0, valorRealizado: 0, variacao: 0 })),
    };

    lista.forEach((bp) => {
        const linha = [
            (bp.contaContabilDescricao || "").trim() + (bp.contaContabilReduzido ? ` (${bp.contaContabilReduzido})` : ""),
            `${bp.centroCustoDescricao || ""} (${bp.centroCustoCodigo || ""})`,
            bp.grupoDescricao || "",
            bp.contaContabilTipo || "",
            bp.filial || "",
            bp.responsavel || "",
        ];

        mesesFiltrados.forEach(({ idx }, i) => {
            const mes = (bp.meses || [])[idx] || {};
            linha.push(mes.valorOrcado || 0, mes.valorRealizado || 0, mes.variacao || 0);
            totais.meses[i].valorOrcado += mes.valorOrcado || 0;
            totais.meses[i].valorRealizado += mes.valorRealizado || 0;
            totais.meses[i].variacao += mes.variacao || 0;
        });

        linha.push(bp.acumuladoOrcado || 0, bp.acumuladoRealizado || 0, bp.acumuladoSaldo || 0);
        linha.push(bp.totalOrcado || 0, bp.totalRealizado || 0, bp.totalSaldo || 0);

        totais.acumuladoOrcado += bp.acumuladoOrcado || 0;
        totais.acumuladoRealizado += bp.acumuladoRealizado || 0;
        totais.acumuladoSaldo += bp.acumuladoSaldo || 0;
        totais.totalOrcado += bp.totalOrcado || 0;
        totais.totalRealizado += bp.totalRealizado || 0;
        totais.totalSaldo += bp.totalSaldo || 0;

        const row = worksheet.addRow(linha);
        row.height = 15.5;

        row.eachCell((cell, colNumber) => {
            cell.font = { name: "Calibri", size: 9 };
            if (colNumber <= COLS_FIXAS.length) {
                cell.alignment = { horizontal: "left", vertical: "middle" };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.linhaFixa } };
            } else {
                cell.alignment = { horizontal: "right", vertical: "middle" };
                cell.numFmt = FORMATO_MOEDA;
                if (colNumber >= inicioAcumulado && colNumber < inicioTotal) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.acumulado.linha } };
                } else if (colNumber >= inicioTotal) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.total.linha } };
                }
            }
        });
    });

    // ---------- Linha de total geral ----------
    const linhaTotal = [
        "TOTAL GERAL", "", "", "", "", "",
        ...totais.meses.flatMap((m) => [m.valorOrcado, m.valorRealizado, m.variacao]),
        totais.acumuladoOrcado, totais.acumuladoRealizado, totais.acumuladoSaldo,
        totais.totalOrcado, totais.totalRealizado, totais.totalSaldo,
    ];

    const rowTotal = worksheet.addRow(linhaTotal);
    rowTotal.height = 18;
    worksheet.mergeCells(rowTotal.number, 1, rowTotal.number, COLS_FIXAS.length);

    rowTotal.eachCell((cell, colNumber) => {
        cell.font = { name: "Calibri", size: 10, bold: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.totalGeral } };
        cell.border = { top: { style: "medium", color: { argb: "FF2C3E50" } } };
        if (colNumber <= COLS_FIXAS.length) {
            cell.alignment = { horizontal: "left", vertical: "middle" };
        } else {
            cell.alignment = { horizontal: "right", vertical: "middle" };
            cell.numFmt = FORMATO_MOEDA;
        }
    });

    const excelBuffer = await workbook.xlsx.writeBuffer();
    const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(fileData, `detalhamento_bp_${ano}_${new Date().getTime()}.xlsx`);
}